import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Button, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, MenuItem, Switch, TextField, TextFieldProps, Typography } from "@mui/material";
import { createTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Moment } from 'moment';
import IOutOffice from "../../types/OutOffice";
import ILog from "../../types/Log";
import { CONFIGTYPES } from "../../types/Config";
import ConfigService from "../../services/ConfigService";
import LogService from "../../services/LogService";
import ConfigUtils from "../../utils/Config";
import { aFecha, ValorFecha } from '../../utils/FechaSelector';
import * as constantes from "../../common/Constantes";

const locale = constantes.locale;

// Estado de un no disponible respecto a ahora. Las reglas son las mismas para todas las clínicas (las valida la API):
// futuro, se edita y se borra; en curso, solo se puede mover el fin (no antes de ahora) y no se borra; pasado, no se toca.
export type EstadoNoDisponible = 'futuro' | 'enCurso' | 'pasado';
export const estadoNoDisponible = (noDisponible: IOutOffice, ahora: number = Date.now()): EstadoNoDisponible => {
  if (new Date(noDisponible.end).getTime() <= ahora) return 'pasado';
  if (new Date(noDisponible.start).getTime() <= ahora) return 'enCurso';
  return 'futuro';
};

// Diálogo para crear o editar un no disponible. Es el mismo en Configuración › Usuarios y, con el registro de jornada,
// en el editor de cada persona.
interface NoDisponibleDialogProps {
  open: boolean;
  noDisponible?: IOutOffice; // editar; sin él, crear
  inicial?: { especialistaId: number; start: Date; end: Date }; // crear con la persona y las fechas ya puestas
  profesionales: { internalID: number; firstName: string }[];
  onClose: () => void;
  onGuardado: (noDisponible: IOutOffice, esNuevo: boolean) => void;
  onError: (mensaje: string) => void;
}

const ahoraEnPunto = () => new Date(new Date().setMinutes(0, 0, 0));
const alMinuto = (fecha: Date) => new Date(Math.floor(fecha.getTime() / 60000) * 60000);
// Todo el día: de las 00:00 del primer día a las 23:59 del último (como lo reconoce textoNoDisponible). Las horas, en la
// del navegador, como el resto del diálogo.
const inicioDelDia = (fecha: Date) => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 0, 0);
const finDelDia = (fecha: Date) => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 23, 59);
const esTodoElDia = (inicio: Date, fin: Date) =>
  inicio.getHours() === 0 && inicio.getMinutes() === 0
  && ((fin.getHours() === 23 && fin.getMinutes() === 59) || (fin.getHours() === 0 && fin.getMinutes() === 0 && fin > inicio));

const NoDisponibleDialog = ({ open, noDisponible, inicial, profesionales, onClose, onGuardado, onError }: NoDisponibleDialogProps) => {
  const theme = createTheme();
  const history = useNavigate();
  const esEdicion = !!noDisponible;
  const enCurso = !!noDisponible && estadoNoDisponible(noDisponible) === 'enCurso';

  const [especialistaId, setEspecialistaId] = useState<number>(0);
  const [start, setStart] = useState<ValorFecha>(ahoraEnPunto());
  const [end, setEnd] = useState<ValorFecha>(ahoraEnPunto());
  const [todoElDia, setTodoElDia] = useState(false);
  const [loading, setLoading] = useState(false);
  // Evita guardar dos veces con un doble clic (el estado loading aún no se ha pintado)
  const guardando = useRef(false);
  // El formulario se abre con inicio = fin (la hora actual): el aviso solo se muestra tras intentar guardar.
  const [intentoGuardar, setIntentoGuardar] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEspecialistaId(noDisponible?.especialistaId ?? inicial?.especialistaId ?? 0);
    const deDiasCompletos = !!noDisponible && esTodoElDia(new Date(noDisponible.start), new Date(noDisponible.end));
    setStart(noDisponible ? noDisponible.start : inicial?.start ?? ahoraEnPunto());
    // Uno de días completos que acaba a las 00:00 tiene como último día el anterior: se muestra ese
    setEnd(noDisponible
      ? (deDiasCompletos ? finDelDia(new Date(new Date(noDisponible.end).getTime() - 60000)) : noDisponible.end)
      : inicial?.end ?? ahoraEnPunto());
    setTodoElDia(deDiasCompletos);
    setIntentoGuardar(false);
    setLoading(false);
    guardando.current = false;
  }, [open, noDisponible, inicial]);

  const inicio = aFecha(start);
  const fin = aFecha(end);
  const errorFechas = !inicio || !fin
    ? (todoElDia ? 'Indica el primer y el último día' : 'Indica la fecha y hora de inicio y de fin')
    : todoElDia
      ? (inicioDelDia(fin) < inicioDelDia(inicio)
        ? 'El último día no puede ser anterior al primero'
        : enCurso && finDelDia(fin).getTime() < Date.now() ? 'Este no disponible ya ha empezado: el último día no puede ser anterior a hoy' : '')
      : fin.getTime() <= inicio.getTime()
        ? 'La fecha final debe ser posterior a la de inicio'
        : enCurso && fin.getTime() < Date.now()
          ? 'Este no disponible ya ha empezado: el fin no puede ser anterior a ahora'
          : '';

  // Al marcar «Todo el día», el periodo pasa a días completos; al desmarcarlo, el fin pasa de las 23:59 a las 00:00 del día
  // siguiente (mismo periodo, y en minutos de 5 en 5 como pide el selector de hora) para ajustar las horas desde ahí
  const cambiarTodoElDia = (marcado: boolean) => {
    setTodoElDia(marcado);
    if (!inicio || !fin) return;
    if (marcado) {
      setStart(inicioDelDia(inicio));
      setEnd(finDelDia(inicioDelDia(fin) < inicioDelDia(inicio) ? inicio : fin));
    } else {
      setEnd(new Date(fin.getFullYear(), fin.getMonth(), fin.getDate() + 1, 0, 0));
    }
  };

  const guardar = () => {
    if (guardando.current) return;
    if (!inicio || !fin || errorFechas) { setIntentoGuardar(true); return; }
    guardando.current = true;
    setLoading(true);

    const start = todoElDia ? (enCurso ? inicio : inicioDelDia(inicio)) : enCurso ? inicio : alMinuto(inicio);
    const end = todoElDia ? finDelDia(fin) : enCurso && alMinuto(fin).getTime() < Date.now() ? new Date() : alMinuto(fin);
    const datos: IOutOffice = esEdicion
      ? { userId: noDisponible!.userId, especialistaId: noDisponible!.especialistaId, start, end }
      : { userId: ConfigUtils.getUserId(), especialistaId, start, end };

    const llamada = esEdicion && noDisponible?._id
      ? ConfigService.updateOutOffice(noDisponible._id, datos)
      : ConfigService.createOutOffice(datos);

    llamada
      .then((response: { data: IOutOffice }) => {
        if (esEdicion) ConfigUtils.editConfig(CONFIGTYPES.outoffices.toString(), response.data);
        else ConfigUtils.addConfig(CONFIGTYPES.outoffices.toString(), response.data);
        onGuardado(response.data, !esEdicion);
      })
      .catch((e: unknown) => {
        const fallo = e as { message?: string; response?: { status?: number; data?: { message?: string } } };
        if (fallo.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else if (fallo.response?.status === 400) {
          // Reglas de la API (fechas, no disponible pasado o ya empezado): se avisa, no es un error
          onError(fallo.response.data?.message || 'No se ha podido guardar el no disponible');
        } else {
          const error: ILog = { method: esEdicion ? 'NoDisponibleDialog.updateOutOffice' : 'NoDisponibleDialog.saveOutOffice', level: 'error', message: fallo.message ?? '', meta: fallo.response };
          LogService.logError(error);
          onError(fallo.response?.data?.message || 'No se ha podido guardar el no disponible');
        }
      })
      .finally(() => {
        setLoading(false);
        guardando.current = false;
        onClose();
      });
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth onClose={onClose}>
      <DialogContent>
        <Typography variant="h4" sx={{ display: 'inline-flex' }}>
          <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><AddIcon /></Avatar><div style={{ margin: '10px auto' }}>{esEdicion ? 'Editar periodo no disponible' : 'Nuevo periodo no disponible'}</div>
        </Typography>

        <Grid container spacing={1}>
          <Grid item md={4} xs={12}>
            <TextField select value={especialistaId} disabled={esEdicion} variant="outlined" label="Usuario" margin="dense" fullWidth required onChange={(event) => setEspecialistaId(parseInt(event.target.value))} >
              {profesionales.map((profesional) => (
                <MenuItem key={profesional.internalID} value={profesional.internalID}>{profesional.firstName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Todo el día: solo el primer y el último día. Uno ya empezado no puede cambiar su inicio, así que no se cambia el modo */}
          {todoElDia ? (
            <>
              <Grid item md={4} xs={12} sx={{ marginTop: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                  <DatePicker
                    renderInput={(props: TextFieldProps) => <TextField required fullWidth {...props} helperText={enCurso ? 'Ya ha empezado: solo se puede cambiar el último día' : undefined} />}
                    label="Primer día"
                    toolbarFormat="dd-mm-yyyy"
                    value={start}
                    disabled={enCurso}
                    onChange={(date: Moment | null) => {
                      const dia = aFecha(date);
                      setStart(dia ? inicioDelDia(dia) : date);
                      if (dia && (!fin || inicioDelDia(fin) < inicioDelDia(dia))) setEnd(finDelDia(dia));
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item md={4} xs={12} sx={{ marginTop: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                  <DatePicker
                    renderInput={(props: TextFieldProps) => <TextField required fullWidth {...props} />}
                    label="Último día"
                    toolbarFormat="dd-mm-yyyy"
                    value={end}
                    onChange={(date: Moment | null) => { const dia = aFecha(date); setEnd(dia ? finDelDia(dia) : date); }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          ) : (
            <>
              <Grid item md={4} xs={12} sx={{ marginTop: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                  <DateTimePicker
                    renderInput={(props: TextFieldProps) => <TextField required fullWidth {...props} helperText={enCurso ? 'Ya ha empezado: solo se puede cambiar el fin' : undefined} />}
                    label="Fecha y hora de inicio"
                    toolbarFormat="dd-mm-yyyy"
                    minutesStep={5}
                    value={start}
                    ampm={false}
                    disabled={enCurso}
                    onChange={(date: Moment | null) => { setStart(date); if (aFecha(date)) setEnd(date); }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item md={4} xs={12} sx={{ marginTop: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                  <DateTimePicker
                    renderInput={(props: TextFieldProps) => <TextField required fullWidth {...props} />}
                    label="Fecha y hora final"
                    toolbarFormat="dd-mm-yyyy"
                    minutesStep={5}
                    value={end}
                    ampm={false}
                    onChange={(date: Moment | null) => setEnd(date)}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={todoElDia} disabled={enCurso} onChange={(e) => cambiarTodoElDia(e.target.checked)} />}
              label="Todo el día"
            />
          </Grid>
          {intentoGuardar && errorFechas && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">{errorFechas}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      {loading ?
        <Grid container>
          <CircularProgress style={{ margin: '20px auto' }} />
        </Grid> :
        <DialogActions>
          <Button onClick={guardar}>Guardar</Button>
          <Button onClick={onClose}>Cancelar</Button>
        </DialogActions>}
    </Dialog>
  );
};

export default NoDisponibleDialog;
